import { queryOptions, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ApiError } from '@/api/errors';
import { clearIntent, intentKey } from '@/api/idempotency';
import type {
  Account,
  CommercialDocument,
  CashReport,
  Cost,
  CostCategory,
  DeliveryRecord,
  LedgerEntry,
  Margin,
  Obligation,
  Outcome,
  Payment,
  PaymentResult,
  SupplierPayment,
  UUID,
  Work,
  WorkEvent,
  WorkStage,
} from '@/api/types';
import type { Decimal } from '@/lib/format/decimal';
import type { LocalDate } from '@/lib/format/dates';
import type { Currency } from '@/lib/format/money';

/** Sub-resources live under the work's detail key, so one invalidation refreshes everything that depends on it. */
export const workKeys = {
  all: ['works'] as const,
  list: (f: object) => ['works', 'list', f] as const,
  detail: (id: UUID) => ['works', 'detail', id] as const,
  part: (id: UUID, part: string) => ['works', 'detail', id, part] as const,
};
export const accountKeys = { all: ['accounts'] as const };
export const cashKeys = { report: (f: object) => ['cash', 'report', f] as const };

export const worksQuery = (f: { stage?: string | null; q?: string }, size = 100) =>
  queryOptions({
    queryKey: workKeys.list({ ...f, size }),
    queryFn: ({ signal }) => api<Work[]>('/works', { query: { stage: f.stage ?? undefined, q: f.q, size }, signal }),
    placeholderData: (p) => p,
  });

export const workQuery = (id: UUID | undefined) =>
  queryOptions({
    queryKey: workKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api<Work>(`/works/${id}`, { signal }),
    enabled: !!id,
  });

function part<T>(id: UUID | undefined, name: string, enabled = true, path = name) {
  return queryOptions({
    queryKey: workKeys.part(id ?? '', name),
    queryFn: ({ signal }) => api<T>(`/works/${id}/${path}`, { signal }),
    enabled: !!id && enabled,
  });
}

export const workPaymentsQuery = (id: UUID | undefined, enabled = true) => part<Payment[]>(id, 'payments', enabled);
export const workLedgerQuery = (id: UUID | undefined, enabled = true) => part<LedgerEntry[]>(id, 'ledger', enabled);
export const workHistoryQuery = (id: UUID | undefined, enabled = true) => part<WorkEvent[]>(id, 'history', enabled);
export const workDeliveriesQuery = (id: UUID | undefined, enabled = true) => part<DeliveryRecord[]>(id, 'deliveries', enabled);
export const workCostsQuery = (id: UUID | undefined, enabled = true) => part<Cost[]>(id, 'costs', enabled);
export const workObligationsQuery = (id: UUID | undefined, enabled = true) => part<Obligation[]>(id, 'supplier-obligations', enabled);
export const workMarginQuery = (id: UUID | undefined, enabled = true) => part<Margin>(id, 'profitability', enabled);
export const workOutcomesQuery = (id: UUID | undefined, enabled = true) => part<Outcome[]>(id, 'material-outcomes', enabled);

/** Receipts and delivery notes the server prepares in the background; polls while any is still being made. */
export const workDocumentsQuery = (id: UUID | undefined, enabled = true) =>
  queryOptions({
    queryKey: workKeys.part(id ?? '', 'documents'),
    queryFn: ({ signal }) => api<CommercialDocument[]>('/documents', { query: { workId: id }, signal }),
    enabled: !!id && enabled,
    refetchInterval: (q) => ((q.state.data ?? []).some((d) => d.status === 'PENDING' || d.status === 'PROCESSING') ? 3000 : false),
  });

export const obligationPaymentsQuery = (workId: UUID, obligationId: UUID, enabled = true) =>
  queryOptions({
    queryKey: workKeys.part(workId, `obligation-payments:${obligationId}`),
    queryFn: ({ signal }) => api<SupplierPayment[]>(`/supplier-obligations/${obligationId}/payments`, { signal }),
    enabled,
  });

export const receivingAccountsQuery = (enabled = true) =>
  queryOptions({
    queryKey: ['accounts', 'receiving'],
    queryFn: ({ signal }) => api<Pick<Account, 'id' | 'name' | 'currency'>[]>('/accounts/receiving', { signal }),
    enabled,
  });

export const accountsQuery = (enabled = true) =>
  queryOptions({
    queryKey: accountKeys.all,
    queryFn: ({ signal }) => api<Account[]>('/accounts', { signal }),
    staleTime: 5 * 60_000,
    enabled,
  });

export const cashReportQuery = (f: { from: LocalDate; to: LocalDate; accountId?: UUID | null }) =>
  queryOptions({
    queryKey: cashKeys.report(f),
    queryFn: ({ signal }) => api<CashReport>('/cash', { query: { from: f.from, to: f.to, accountId: f.accountId ?? undefined }, signal }),
    placeholderData: (p) => p,
  });

export const STAGES = ['MEASUREMENT', 'PROCUREMENT', 'CUTTING', 'SANDING', 'POLISHING', 'READY', 'INSTALLATION', 'DELIVERED', 'CLOSED'] as const;
/** Stages a person can move a work to by hand: after the current one and before delivery (that one comes with the delivery). */
export const ADVANCE_TARGETS: WorkStage[] = ['PROCUREMENT', 'CUTTING', 'SANDING', 'POLISHING', 'READY', 'INSTALLATION'];

/** Everything about one work (its money, costs, history…) may have changed. */
function refreshWork(client: QueryClient, id: UUID) {
  void client.invalidateQueries({ queryKey: workKeys.detail(id) });
  void client.invalidateQueries({ queryKey: ['works', 'list'] });
}

function useWorkWrite<TVars extends { work: Work }>(fn: (vars: TVars) => Promise<Work>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (work, vars) => {
      refreshWork(client, vars.work.id);
      client.setQueryData(workKeys.detail(work.id), work);
    },
    // Someone else edited it: show their version right away so the next attempt uses the current `version`.
    onError: (error, vars) => {
      if (error instanceof ApiError && error.is('STALE_VERSION', 'CONCURRENT_EDIT')) refreshWork(client, vars.work.id);
    },
  });
}

export type WorkAction = 'measure' | 'deposit-exception' | 'pause' | 'resume' | 'advance' | 'costs-complete' | 'close' | 'reopen';

export function useWorkAction() {
  return useWorkWrite(({ work, action, reason, target }: { work: Work; action: WorkAction; reason: string; target?: WorkStage }) =>
    api<Work>(`/works/${work.id}/actions/${action}`, { body: { version: work.version, reason, ...(target ? { target } : {}) } }),
  );
}

export function useCancelWork() {
  return useWorkWrite(({ work, finalCharge, reason }: { work: Work; finalCharge: Decimal; reason: string }) => {
    const scope = `work-cancel:${work.id}`;
    const body = { version: work.version, finalCharge, reason };
    return api<Work>(`/works/${work.id}/cancel`, { body, idempotencyKey: intentKey(scope, body) }).then((w) => {
      clearIntent(scope);
      return w;
    });
  });
}

export interface DeliveryResult {
  work: Work;
  deliveredQuantities: Record<UUID, Decimal>;
}

export function useDeliver() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ work, pieces, evidence }: { work: Work; pieces: { pieceId: UUID; quantity: number }[]; evidence: string }) => {
      const scope = `work-delivery:${work.id}`;
      const body = { revisionId: work.revisionId, pieces, evidence };
      const result = await api<DeliveryResult>(`/works/${work.id}/deliveries`, { body, idempotencyKey: intentKey(scope, body) });
      clearIntent(scope);
      return result;
    },
    onSuccess: ({ work }) => {
      refreshWork(client, work.id);
      client.setQueryData(workKeys.detail(work.id), work);
    },
  });
}

export interface PaymentInput {
  amount: Decimal;
  currency: Currency;
  accountId: UUID;
  fxRateId?: UUID | null;
  fxOverrideReason?: string | null;
  method: string;
  reference: string;
  paidAt: string;
  note?: string | null;
}

export function useReportPayment(workId: UUID) {
  const client = useQueryClient();
  // This endpoint has no idempotency key: the server refuses an identical report, and the button stays busy while it runs.
  return useMutation({
    mutationFn: (input: PaymentInput) => api<Payment>(`/works/${workId}/payments`, { body: { ...input, fxRateId: input.fxRateId ?? undefined } }),
    onSuccess: () => refreshWork(client, workId),
  });
}

export function useConfirmPayment(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId, fxRateId, fxOverrideReason }: { paymentId: UUID; fxRateId?: UUID | null; fxOverrideReason?: string | null }) => {
      const scope = `payment-confirm:${paymentId}`;
      const result = await api<PaymentResult>(`/payments/${paymentId}/confirm`, {
        method: 'POST',
        query: { fxRateId: fxRateId ?? undefined, fxOverrideReason: fxOverrideReason?.trim() || undefined },
        idempotencyKey: intentKey(scope, { fxRateId: fxRateId ?? null, fxOverrideReason: fxOverrideReason?.trim() || null }),
      });
      clearIntent(scope);
      return result;
    },
    onSuccess: () => refreshWork(client, workId),
  });
}

export function useRejectPayment(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: UUID; reason: string }) => api<Payment>(`/payments/${paymentId}/reject`, { body: { reason } }),
    onSuccess: () => refreshWork(client, workId),
  });
}

export function useReversePayment(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId, reason }: { paymentId: UUID; reason: string }) => {
      const scope = `payment-reverse:${paymentId}`;
      const result = await api<PaymentResult>(`/payments/${paymentId}/reverse`, { body: { reason }, idempotencyKey: intentKey(scope, { reason }) });
      clearIntent(scope);
      return result;
    },
    onSuccess: () => {
      refreshWork(client, workId);
      void client.invalidateQueries({ queryKey: ['cash'] });
    },
  });
}

export function useRefund(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { paymentId: UUID; accountId: UUID; amount: Decimal; reason: string }) => {
      const scope = `work-refund:${workId}`;
      const result = await api<{ refundId: UUID }>(`/works/${workId}/refunds`, { body: input, idempotencyKey: intentKey(scope, input) });
      clearIntent(scope);
      return result;
    },
    onSuccess: () => {
      refreshWork(client, workId);
      void client.invalidateQueries({ queryKey: ['cash'] });
    },
  });
}

export function useCreateAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { ownerId: UUID; name: string; currency: Currency; kind: 'BANK' | 'CASH' }) => api<Account>('/accounts', { body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: accountKeys.all }),
  });
}

export interface CostInput {
  category: CostCategory;
  description: string;
  amount: Decimal;
  currency: Currency;
  fxRateId?: UUID | null;
  fxOverrideReason?: string | null;
}

export function useAddCost(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: CostInput) => {
      const scope = `work-cost:${workId}`;
      const body = { ...input, fxRateId: input.fxRateId ?? undefined };
      const cost = await api<Cost>(`/works/${workId}/costs`, { body, idempotencyKey: intentKey(scope, body) });
      clearIntent(scope);
      return cost;
    },
    onSuccess: () => refreshWork(client, workId),
  });
}

export function useReverseCost(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ costId, reason }: { costId: UUID; reason: string }) => api<Cost>(`/costs/${costId}/reverse`, { body: { reason } }),
    onSuccess: () => refreshWork(client, workId),
  });
}

export interface ObligationInput {
  supplierId: UUID;
  currency: Currency;
  amount: Decimal;
  fxRateId?: UUID | null;
  fxOverrideReason?: string | null;
  reference: string;
  dueDate?: LocalDate | null;
  recognizeCost: boolean;
  costDescription?: string | null;
}

export function useAddObligation(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ObligationInput) =>
      api<Obligation>(`/works/${workId}/supplier-obligations`, {
        body: { ...input, fxRateId: input.fxRateId ?? undefined, dueDate: input.dueDate ?? undefined, costDescription: input.costDescription || undefined },
      }),
    onSuccess: () => refreshWork(client, workId),
  });
}

export function usePayObligation(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ obligationId, ...input }: { obligationId: UUID; accountId: UUID; currency: Currency; amount: Decimal; fxRateId?: UUID | null; fxOverrideReason?: string | null; reason: string }) => {
      const scope = `supplier-pay:${obligationId}`;
      const body = { ...input, fxRateId: input.fxRateId ?? undefined };
      const result = await api<{ supplierPaymentId: UUID; obligation: Obligation }>(`/supplier-obligations/${obligationId}/payments`, { body, idempotencyKey: intentKey(scope, body) });
      clearIntent(scope);
      return result;
    },
    onSuccess: () => {
      refreshWork(client, workId);
      void client.invalidateQueries({ queryKey: ['cash'] });
    },
  });
}

export function useReverseSupplierPayment(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId, reason }: { paymentId: UUID; reason: string }) => {
      const scope = `supplier-reverse:${paymentId}`;
      const result = await api<{ reversalId: UUID }>(`/supplier-payments/${paymentId}/reverse`, { body: { reason }, idempotencyKey: intentKey(scope, { reason }) });
      clearIntent(scope);
      return result;
    },
    onSuccess: () => {
      refreshWork(client, workId);
      void client.invalidateQueries({ queryKey: ['cash'] });
    },
  });
}

export function useCancelObligation(workId: UUID) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ obligationId, reason }: { obligationId: UUID; reason: string }) => api<Obligation>(`/supplier-obligations/${obligationId}/cancel`, { body: { reason } }),
    onSuccess: () => refreshWork(client, workId),
  });
}
